"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ListMusic, Loader2, Pause, Play, Send, SquarePen } from "lucide-react";
import { motion, useTransform } from "motion/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { addProfileSong } from "@/actions/music";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer/regular-post-drawer.client";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { profileKeys } from "@/features/profile/queries/keys";
import { useShellProfileData } from "@/hooks/use-shell-profile-data";
import { mediaPreviewUrlFromAsset } from "@/lib/media";
import type { MusicAttachment } from "@/lib/music/attachment";
import { musicArtworkSrc } from "@/lib/music/links";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { musicKeys } from "./music-browser";
import { MusicServiceChips } from "./music-service-chips";
import { PlayingBars } from "./playing-bars";
import { useMusicPreview } from "./use-music-preview";

/**
 * Everything you can do with a song, from wherever you met it — a post's
 * music pill, a banner, a chat song card, someone's Music picks:
 *
 *   play the preview · listen on Apple Music / Spotify / YouTube Music ·
 *   send it in a chat · post with it · add it to your own Music picks
 *
 * Signed-out viewers get the first two. Every sub-flow stays inside this
 * sheet's own tree (no route change), and clicks are stopped at the content so
 * a sheet opened from inside a card link never also follows the link.
 */
export function SongSheet({
  open,
  onOpenChange,
  music,
  nested = false,
  layer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  music: MusicAttachment;
  /** Opened from inside another drawer (e.g. a profile's full list). */
  nested?: boolean;
  /** Overlay z-index when opened above a high-layer drawer (comments). */
  layer?: number;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const shell = useShellProfileData();
  const me = shell.data ?? null;
  const preview = useMusicPreview();
  const [sendOpen, setSendOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const playing =
    preview.key === music.songId &&
    (preview.status === "playing" || preview.status === "loading");
  const progressWidth = useTransform(preview.progress, (p) => `${p * 100}%`);
  const art = musicArtworkSrc(music.artworkUrl, 320);
  const tint = music.artworkBgColor ? `#${music.artworkBgColor}` : "#3b2f63";

  const addToPicks = useMutation({
    mutationFn: async () => {
      const result = await addProfileSong({ songId: music.songId });
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    onSuccess: async (data) => {
      uiHaptics.success();
      // Past the display, a save still works — it lands in the list behind
      // "+N" — and the toast says so, so "full" never reads as a failure.
      if (!data.added) {
        toast.message("Already in your Music picks");
      } else if (data.featured) {
        toast.success("Added to your Music picks");
      } else {
        toast.success("Saved to your Music picks list", {
          description: `It's #${data.songs.length} — move it up anytime to show it on your profile.`,
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: musicKeys.mine() }),
        queryClient.invalidateQueries({
          queryKey: profileKeys.currentProfile(),
        }),
      ]);
      router.refresh();
    },
    onError: (error) => {
      uiHaptics.error();
      toast.error(error instanceof Error ? error.message : "Couldn't add it");
    },
  });

  // Close this sheet before opening the next one, as Control Center does —
  // two stacked vaul sheets fight over the body lock.
  const handOff = (open: () => void) => {
    preview.pause();
    onOpenChange(false);
    setTimeout(open, 220);
  };

  return (
    <>
      <Drawer
        open={open}
        nested={nested}
        onOpenChange={(next) => {
          if (!next) preview.pause();
          onOpenChange(next);
        }}
      >
        <DrawerContent
          layer={layer}
          className="mx-auto max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:rounded-t-3xl"
          onClick={(event) => event.stopPropagation()}
        >
          <DrawerTitle className="sr-only">
            {music.title} by {music.artistName}
          </DrawerTitle>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-56 opacity-60"
            style={{
              background: `radial-gradient(120% 90% at 50% 0%, ${tint} 0%, transparent 70%)`,
            }}
          />
          <div className="relative flex flex-col items-center gap-4 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
            <div className="relative size-40 overflow-hidden rounded-2xl bg-surface-alt shadow-xl">
              {art ? (
                // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                <img src={art} alt="" className="h-full w-full object-cover" />
              ) : null}
            </div>
            <div className="flex w-full min-w-0 flex-col items-center gap-0.5 text-center">
              <p className="flex max-w-full items-center gap-1.5 text-lg leading-tight font-bold text-text-primary">
                <span className="truncate">{music.title}</span>
                {music.explicit ? (
                  <span className="shrink-0 rounded-[3px] bg-text-tertiary/25 px-1 text-[10px] leading-4 font-bold text-text-secondary">
                    E
                  </span>
                ) : null}
              </p>
              <p className="max-w-full truncate text-sm text-text-secondary">
                {music.artistName}
                {music.albumName ? ` · ${music.albumName}` : ""}
              </p>
            </div>

            <div className="flex w-full items-center gap-3">
              <button
                type="button"
                disabled={!music.previewUrl}
                onClick={() =>
                  preview.toggle({
                    key: music.songId,
                    previewUrl: music.previewUrl,
                    surface: "sheet",
                  })
                }
                aria-label={playing ? "Pause preview" : "Play preview"}
                className="flex size-12 shrink-0 items-center justify-center rounded-full bg-text-primary text-background shadow-md transition-transform duration-150 ease-out-strong active:scale-95 disabled:opacity-40"
              >
                {preview.status === "loading" && playing ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : playing ? (
                  <Pause
                    className="size-5"
                    fill="currentColor"
                    strokeWidth={0}
                  />
                ) : (
                  <Play
                    className="ml-0.5 size-5"
                    fill="currentColor"
                    strokeWidth={0}
                  />
                )}
              </button>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="h-1 w-full overflow-hidden rounded-full bg-text-tertiary/20">
                  <motion.div
                    className="h-full rounded-full bg-text-primary"
                    style={{
                      width:
                        preview.key === music.songId ? progressWidth : "0%",
                    }}
                  />
                </div>
                <p className="flex h-4 items-center gap-1.5 text-[11px] text-text-tertiary">
                  {playing ? (
                    <PlayingBars className="h-2.5 text-brand" />
                  ) : null}
                  {music.previewUrl
                    ? "30-second preview"
                    : "No preview for this one"}
                </p>
              </div>
            </div>

            <MusicServiceChips
              song={music}
              className="scrollbar-hide w-full flex-nowrap justify-center-safe overflow-x-auto"
            />

            {me ? (
              // Above a high-layer drawer (a comments sheet) the chat and
              // composer sheets would open UNDER it, so only actions that
              // need no further sheet are offered there.
              <div
                className={cn(
                  "grid w-full gap-2",
                  layer === undefined ? "grid-cols-3" : "grid-cols-1",
                )}
              >
                {layer === undefined ? (
                  <>
                    <SheetAction
                      icon={Send}
                      label="Send in chat"
                      onClick={() => handOff(() => setSendOpen(true))}
                    />
                    <SheetAction
                      icon={SquarePen}
                      label="Post with it"
                      onClick={() => handOff(() => setComposerOpen(true))}
                    />
                  </>
                ) : null}
                <SheetAction
                  icon={ListMusic}
                  label="Add to picks"
                  busy={addToPicks.isPending}
                  onClick={() => addToPicks.mutate()}
                />
              </div>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>

      {me ? (
        <>
          <SendToChatDrawer
            open={sendOpen}
            onOpenChange={setSendOpen}
            payload={sendOpen ? { type: "song", song: music } : null}
          />
          <RegularPostDrawer
            open={composerOpen}
            onOpenChange={setComposerOpen}
            initialMusic={music}
            currentUser={{
              id: me.userId,
              displayName: me.fullName ?? "You",
              avatarUrl: mediaPreviewUrlFromAsset(me.images?.[0]) ?? null,
            }}
          />
        </>
      ) : null}
    </>
  );
}

function SheetAction({
  icon: Icon,
  label,
  onClick,
  busy = false,
}: {
  icon: typeof Send;
  label: string;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={cn(
        "flex h-[4.25rem] flex-col items-center justify-center gap-1.5 rounded-2xl border border-border bg-surface-alt text-xs font-semibold text-text-secondary transition-transform duration-150 ease-out-strong active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white",
      )}
    >
      {busy ? (
        <Loader2 className="size-5 animate-spin" />
      ) : (
        <Icon className="size-5" />
      )}
      {label}
    </button>
  );
}
